import { useState } from 'react';
import api from 'services/api';
import AvisoModal from 'components/AvisoModal';
import SearchableSelect from 'components/SearchableSelect';
import PasswordInput from 'components/PasswordInput';
import { ESTADO_ASAMBLEA as ASM_STATUS_LABEL } from 'utils/etiquetas';
import styles from './AssemblyAdminModal.module.css';
import { EMAIL_REGEX } from 'utils/email';

export const assemblyAdminService = {
  create: (data) => api.post('/property-admins/assembly-admin', data),
  assign: (data) => api.post('/property-admins/assembly-admin/assign', data),
  update: (id, data) => api.put(`/property-admins/assembly-admin/${id}`, data),
  delete: (id) => api.delete(`/property-admins/assembly-admin/${id}`),
};


// Mismas reglas y mensajes que valida el backend (propertyAdminsController.validarCamposAdmin): el formulario no tenía noValidate ni validación propia,
// así que el navegador bloqueaba el envío con su burbuja nativa y la app nunca mostraba el aviso.
function validarCampos(form, editando) {
  if (!form.nombre.trim()) return 'El nombre es requerido';
  if (!editando) {
    if (!form.email.trim()) return 'El email es requerido';
    if (!EMAIL_REGEX.test(form.email.trim())) return 'El correo no es válido';
  }
  return null;
}

export default function AssemblyAdminModal({ editing, assemblies, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre:      editing?.user?.name      || '',
    apellido:    editing?.user?.last_name || '',
    email:       editing?.user?.email     || '',
    telefono:    editing?.user?.phone     || '',
    password:    '',
    assembly_id: editing?.assembly_id     || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const campoError = validarCampos(form, !!editing);
    if (campoError) return setError(campoError);
    setError(''); setSaving(true);
    try {
      if (editing) {
        await assemblyAdminService.update(editing.id, {
          nombre: form.nombre, apellido: form.apellido, telefono: form.telefono,
          assembly_id: form.assembly_id || null,
        });
        onSaved('Admin de asamblea actualizado');
      } else {
        const res = await assemblyAdminService.create(form);
        const tempPass = res.data?.temp_password;
        onSaved(
          'Admin de asamblea creado. Se envió email con credenciales.',
          tempPass ? { email: form.email, password: tempPass } : null
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalAdminAsamblea}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>
              <i className={`fas fa-user-tie ${styles.iconoTitulo}`} />
              {editing ? 'Editar' : 'Nuevo'} admin de asamblea
            </h3>
            <div className={styles.subtitulo}>
              Acceso solo a gestión de asambleas
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={save} noValidate>
          <div className={`modal-body ${styles.cuerpo}`}>
            {error && <AvisoModal>{error}</AvisoModal>}

            <div className={styles.rejilla}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-control" value={form.nombre} onChange={e => set('nombre', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido</label>
                <input className="form-control" value={form.apellido} onChange={e => set('apellido', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Correo electrónico *</label>
              <input type="email" className="form-control" value={form.email}
                onChange={e => set('email', e.target.value)} required disabled={!!editing} />
              {editing && <div className={styles.nota}>El email no puede modificarse</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-control" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="3001234567" />
            </div>

            {!editing && (
              <div className="form-group">
                <label className="form-label">Contraseña (opcional)</label>
                <PasswordInput value={form.password} autoComplete="new-password"
                  onChange={e => set('password', e.target.value)} placeholder="Mínimo 8 caracteres" />
                <div className={styles.nota}>
                  Si la dejas vacía, se genera una temporal y se envía por correo
                </div>
              </div>
            )}

            <div className={`form-group ${styles.grupoSinMargen}`}>
              <label className="form-label">Asamblea asignada</label>
              <SearchableSelect value={form.assembly_id} onChange={(id) => set('assembly_id', id)} placeholder="— Sin asignar —" minWidth={0} maxWidth={9999} ariaLabel="Asamblea asignada"
                options={assemblies.map(a => ({ value: a.id, label: `#${a.number_assembly} · ${a.property?.name || `Propiedad #${a.property_id}`}${a.status ? ` [${ASM_STATUS_LABEL[a.status] || a.status}]` : ''}` }))} />
              <div className={styles.nota}>
                Puedes asignar la asamblea después
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className={`btn btn-primary ${styles.botonGuardar}`} disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Guardando...</> : <><i className="fas fa-save" /> Guardar</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
